class ShareDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#ShareDrawer-Overlay').addEventListener('click', this.close.bind(this));
    
    // Initialize elements
    this.elements = {
      urlInput: this.querySelector('#ShareDrawer-Url'),
      copyButton: this.querySelector('[data-copy-url]'),
      message: this.querySelector('#ShareDrawer-Message'),
      socialButtons: this.querySelectorAll('[data-social]'),
      nativeButton: this.querySelector('.share-drawer__native-button'),
      nativeSection: this.querySelector('.share-drawer__native-section')
    };

    // Set up event listeners
    this.setupEventListeners();
    
    // Check for native sharing support
    this.checkNativeShareSupport();
    
    // Set initial URL
    this.currentUrl = window.location.href;
    this.currentTitle = document.title;
    this.updateUrl(this.currentUrl);
  }

  setupEventListeners() {
    // Copy button
    if (this.elements.copyButton) {
      this.elements.copyButton.addEventListener('click', this.copyToClipboard.bind(this));
    }

    // Social sharing buttons
    this.elements.socialButtons.forEach(button => {
      button.addEventListener('click', this.handleSocialShare.bind(this));
    });

    // Native share button
    if (this.elements.nativeButton) {
      this.elements.nativeButton.addEventListener('click', this.handleNativeShare.bind(this));
    }
  }

  checkNativeShareSupport() {
    if (navigator.share && this.elements.nativeSection) {
      this.elements.nativeSection.style.display = 'block';
    }
  }

  open(triggeredBy, url = null, title = null) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    
    // Update URL and title if provided
    if (url) {
      this.currentUrl = url;
      this.updateUrl(url);
    }
    if (title) {
      this.currentTitle = title;
    }

    // Show drawer with animation
    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 10);

    // Set up focus trap
    this.addEventListener(
      'transitionend',
      () => {
        const focusElement = this.querySelector('.drawer__close');
        trapFocus(this, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.hideMessage();
    
    if (this.activeElement) {
      removeTrapFocus(this.activeElement);
    }
    
    document.body.classList.remove('overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateUrl(url) {
    this.currentUrl = url;
    if (this.elements.urlInput) {
      this.elements.urlInput.value = url;
    }
  }

  copyToClipboard(e) {
    e.preventDefault();
    if (!this.elements.urlInput) return;

    // Modern browsers with clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(this.elements.urlInput.value).then(() => {
        this.showMessage('Link copied to clipboard!');
      }).catch(() => {
        this.fallbackCopyToClipboard();
      });
    } else {
      this.fallbackCopyToClipboard();
    }
  }

  fallbackCopyToClipboard() {
    try {
      this.elements.urlInput.select();
      this.elements.urlInput.setSelectionRange(0, 99999);
      document.execCommand('copy');
      this.showMessage('Link copied to clipboard!');
    } catch (err) {
      this.showMessage('Unable to copy link. Please copy manually.');
    }
  }

  handleSocialShare(event) {
    event.preventDefault();
    const platform = event.currentTarget.dataset.social;
    const url = encodeURIComponent(this.currentUrl);
    const title = encodeURIComponent(this.currentTitle);
    
    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'pinterest':
        // Get product image if available
        const productImage = document.querySelector('.product__media img');
        const imageUrl = productImage ? encodeURIComponent(productImage.src) : '';
        shareUrl = `https://pinterest.com/pin/create/button/?url=${url}&description=${title}&media=${imageUrl}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${title}%20${url}`;
        break;
      case 'email':
        shareUrl = `mailto:?subject=${title}&body=Check%20out%20this%20product:%20${url}`;
        break;
    }

    if (shareUrl) {
      if (platform === 'email') {
        window.location.href = shareUrl;
      } else {
        window.open(shareUrl, '_blank', 'width=600,height=400');
      }
    }
  }

  handleNativeShare() {
    if (navigator.share) {
      navigator.share({
        title: this.currentTitle,
        url: this.currentUrl
      }).then(() => {
        this.showMessage('Shared successfully!');
      }).catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      });
    }
  }

  showMessage(text) {
    if (!this.elements.message) return;
    
    this.elements.message.textContent = text;
    this.elements.message.classList.add('show');
    
    setTimeout(() => {
      this.hideMessage();
    }, 3000);
  }

  hideMessage() {
    if (this.elements.message) {
      this.elements.message.classList.remove('show');
    }
  }
}

// Register the custom element
if (!customElements.get('share-drawer')) {
  customElements.define('share-drawer', ShareDrawer);
}

// Helper function to open share drawer from anywhere
window.openShareDrawer = function(triggeredBy, url = null, title = null) {
  const shareDrawer = document.querySelector('share-drawer');
  if (shareDrawer) {
    shareDrawer.open(triggeredBy, url, title);
  }
};
